import { ReactElement, useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutes, useAwsEffect, useAwsGetter, useAwsBatchIterator } from '@localstack/services';
import { Search as SearchIcon } from '@mui/icons-material';
import { Card, Button, Box, InputAdornment, TextField } from '@mui/material';

import { Dropdown, ButtonWithDialog, ECSTasksTable, PageTitle } from '@localstack/ui';

import { DEFAULT_ECS_ROUTES } from '@localstack/constants';

import { ECSProps } from './types';

import { ClusterNavTabs } from './components';

const POLLING_INTERVAL = 5000;

const getECSTaskServiceName = ({ group, startedBy }: { group?: string; startedBy?: string }): string => {
  const extractServiceName = (value?: string): string =>
    value?.startsWith('service:') ? value.replace('service:', '') : '';

  return extractServiceName(group) || extractServiceName(startedBy);
};

export const ECSClusterTasks = ({ Layout, clientOverrides, routes = DEFAULT_ECS_ROUTES }: ECSProps): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { clusterName } = useParams<'clusterName'>();

  const { goto } = useRoutes();
  const { describeTasks } = useAwsEffect('ECS', ['describeTasks'], { clientOverrides });

  const {
    data: tasksArns,
    isLoading,
    mutate,
  } = useAwsGetter('ECS', 'listTasks', [{ cluster: clusterName }], {
    swrOverrides: { refreshInterval: POLLING_INTERVAL },
    clientOverrides,
  });

  const tasks = useAwsBatchIterator(
    tasksArns?.taskArns,
    async (arn) => (await describeTasks({ cluster: clusterName, tasks: [arn] })).tasks ?? [],
  );

  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
  const filteredTasks = useMemo(
    () =>
      tasks.filter((task) => {
        if (!normalizedSearchQuery) {
          return true;
        }

        return [task.taskArn, task.lastStatus, task.desiredStatus, getECSTaskServiceName(task)].some((value) =>
          value?.toLocaleLowerCase().includes(normalizedSearchQuery),
        );
      }),
    [normalizedSearchQuery, tasks],
  );
  const visibleTaskArns = useMemo(
    () => new Set(filteredTasks.map(({ taskArn }) => taskArn).filter(Boolean)),
    [filteredTasks],
  );

  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => visibleTaskArns.has(id));
      return next.length === current.length ? current : next;
    });
  }, [visibleTaskArns]);

  const { stopTask } = useAwsEffect('ECS', ['startTask', 'stopTask', 'runTask'], {
    revalidate: ['listTasks', 'describeTasks'],
    clientOverrides,
  });

  const handleStopSelected = useCallback(async () => {
    const promises = selectedIds.map((task) => stopTask({ cluster: clusterName as string, task }));
    await Promise.all(promises);
  }, [clusterName, selectedIds, stopTask]);

  return (
    <Layout
      documentTitle="Cluster Tasks"
      tabs={<ClusterNavTabs clusterName={clusterName as string} routes={routes} />}
      title={
        <PageTitle
          title="Cluster Tasks"
          onMutate={mutate}
          breadcrumbs={[
            ['ECS', () => goto(routes.RESOURCES_ECS)],
            ['Clusters', () => goto(routes.RESOURCES_ECS_CLUSTERS)],
            [clusterName, () => goto(routes.RESOURCES_ECS_CLUSTER, { clusterName })],
            ['Tasks', null],
          ]}
        />
      }
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_ECS_CLUSTER_TASK_DEPLOY, { clusterName })}>
            Start new Task
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Stop ${selectedIds.length} Task(s)?`}
              onClick={handleStopSelected}
              dialogContent="Selected Tasks will be stopped"
            >
              Stop Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <Box px={2} pt={2}>
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            placeholder="Search tasks by service, ARN, or status"
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <ECSTasksTable
          tasks={filteredTasks}
          loading={isLoading}
          onSelect={setSelectedIds}
          onViewTask={({ taskArn }) => goto(routes.RESOURCES_ECS_CLUSTER_TASK, { clusterName, arn: taskArn })}
        />
      </Card>
    </Layout>
  );
};
